// src/lib/markdown/renderInlineMarkdown.tsx

import Link from 'next/link'
import type { ReactNode } from 'react'

/**
 * Renders lightweight inline markdown: `**bold**` and `[label](href)` only.
 * Used in generator intros/FAQs where full MDX is unnecessary.
 *
 * @param {string} text - Source text with optional inline markdown.
 * @returns {ReactNode[]} Fragment-ready nodes for embedding in a paragraph.
 */
export function renderInlineMarkdown(text: string): ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/)
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={index} className="font-semibold text-gray-900 dark:text-white">
          {part.slice(2, -2)}
        </strong>
      )
    }
    const linkPattern = /^\[([^\]]+)\]\(([^)]+)\)$/
    const linkMatch = linkPattern.exec(part)
    if (linkMatch?.[1] && linkMatch[2]) {
      const href = linkMatch[2]
      // Scheme allowlist: only internal paths and http(s). Anything else
      // (e.g. a stray `javascript:` from a bad edit/merge) renders as plain
      // text rather than flowing an unsafe href into <Link>.
      const isSafeHref = href.startsWith('/') || /^https?:\/\//i.test(href)
      if (!isSafeHref) {
        return linkMatch[1]
      }
      const isExternal = /^https?:\/\//i.test(href)
      return (
        <Link
          key={index}
          href={href}
          className="font-medium text-indigo-600 underline decoration-indigo-300 underline-offset-2 hover:text-indigo-500 dark:text-indigo-400"
          {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {linkMatch[1]}
        </Link>
      )
    }
    return part
  })
}
